/**
 * cellWorkflow.js — reading and writing a cell's workflow, which is an EMBEDDED COPY.
 *
 * `cell.props.cell_workflow` (and a mission's default) holds the body itself — `{ alias, nodes, edges }`
 * — not a reference to a palette entry. That is deliberate and load-bearing: editing a library entry
 * must not reshape a cell that is already running, so the orchestrator interprets the cell's own copy
 * and the copy is frozen the first time the cell enters `working`. An alias would make every in-flight
 * cell's graph shift under it the moment somebody tidied the palette.
 *
 * Two consequences this module exists to absorb:
 *
 *  - **Anything rendering the value must render `.alias`**, never the value. Passing the body to JSX
 *    is what produced "Objects are not valid as a React child (found: object with keys
 *    {alias, edges, description, nodes})" — a whole widget taken down by one interpolation.
 *  - **Anything picking a workflow must write the body**, resolved from the palette at pick time.
 *
 * Legacy cells authored before the embed decision carry a bare alias string. Reading tolerates that;
 * writing never produces it. That is a one-line concession instead of a data migration.
 */

/** The alias of a workflow value — the embedded body's `alias`, or a legacy bare string. */
export function workflowAlias(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return typeof value.alias === 'string' ? value.alias : '';
}

/** True when the value carries an interpretable step graph rather than just an alias. */
export function isWorkflowBody(value) {
  return !!value && typeof value === 'object' && Array.isArray(value.nodes);
}

/**
 * The step graph to interpret for a cell.
 *
 * The embedded copy wins — always, and even when the palette has an entry of the same alias, because
 * the copy is what the orchestrator walks. The palette is consulted only to resolve a legacy alias.
 *
 * @param {Object|string|null} value - cell.props.cell_workflow
 * @param {Array} workflows - the `app/missions/cell_workflows` palette
 * @returns {Object|null}
 */
export function workflowBody(value, workflows) {
  if (isWorkflowBody(value)) return value;
  const alias = workflowAlias(value);
  if (!alias || !Array.isArray(workflows)) return null;
  return workflows.find(entry => entry && entry.alias === alias) || null;
}

/**
 * The value to WRITE when someone picks `alias` from the palette: the body, copied.
 * Returns null for a cleared selection, and null for an alias the palette does not have — writing a
 * bare alias would recreate the reference semantics this embed exists to avoid.
 */
export function workflowSelection(alias, workflows) {
  if (!alias || !Array.isArray(workflows)) return null;
  const entry = workflows.find(candidate => candidate && candidate.alias === alias);
  return entry ? { ...entry } : null;
}


/**
 * What the workspace picker should show for the workspace a cell HAS.
 *
 * A cell embeds a COPY of its workspace and keeps no reference to the palette entry it came from, so
 * the two drift apart the moment the palette is edited — an entry re-aliased, a folder corrected, a
 * cell created before either. The picker used to reverse-match by `work_folder` and fall back to
 * "— none —", which is a lie: the cell has a workspace and its next run will use it.
 *
 * Returns `{ alias }` when the palette still has it, `{ folder }` when it does not, and null when the
 * cell genuinely has no workspace — which the caller renders as "— none —", the one case that means it.
 */
export function workspaceSelection(workspace, workspaces) {
  const folder = workspace && workspace.work_folder;
  if (!folder) return null;
  const entry = (Array.isArray(workspaces) ? workspaces : [])
    .find(candidate => candidate && candidate.work_folder === folder);
  return entry && entry.alias ? { alias: entry.alias } : { folder };
}
